/**
 * TermsOfService component displays the company's legal terms and conditions
 * @component
 * @description Provides comprehensive information about:
 * - Service usage terms and conditions
 * - Product-specific terms for all three services
 * - Account terms and requirements
 * - Payment terms and billing
 * - Acceptable use policies
 * - Termination conditions
 * - Service level expectations
 * @returns {JSX.Element} The rendered TermsOfService component
 */

import React from 'react';
import './Policy.css';

const TermsOfService = () => {
  return (
    <div className="policy-container">
      <h1>Terms of Service</h1>
      <p className="last-updated">Last Updated: August 30, 2025</p>

      <section>
        <h2>1. Agreement to Terms</h2>
        <p>
          By accessing or using TearFace's website and services (including the Accessibility Widget, Chat Support Widget, and Digital Portfolio Services), you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not use our services.
        </p>
      </section>

      <section>
        <h2>2. Service-Specific Terms</h2>
        
        <h3>2.1 Accessibility Widget</h3>
        <ul>
          <li>Widget Features:
            <ul>
              <li>Our widget provides accessibility enhancement options including text sizing, contrast adjustments, and navigation aids</li>
              <li>Features are provided on an "as-is" basis without warranties of any kind, whether express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement</li>
              <li>The auto-adjustment feature applies pre-configured accessibility settings</li>
              <li>Effectiveness may vary based on your website's structure and content</li>
              <li>Information provided in our FAQs, website, or other materials is for general informational purposes only and does not constitute legal, compliance, or accessibility advice</li>
            </ul>
          </li>
          <li>Data and Storage:
            <ul>
              <li>User preferences are stored in browser local storage</li>
              <li>Basic usage analytics may be collected</li>
              <li>No personal user data is collected or stored by the widget</li>
            </ul>
          </li>
          <li>Usage Terms:
            <ul>
              <li>Widget must be implemented according to our documentation</li>
              <li>Usage is limited to domains specified during registration</li>
              <li>You may not modify or reverse engineer the widget</li>
              <li>"Free to use" means you may run the widget on your authorized domains only</li>
              <li>You may not download, store, or redistribute the widget files</li>
              <li>This applies to files from any source (Chrome Web Store, Mozilla Add-ons, or our servers)</li>
              <li>You must use the latest version as provided through official channels</li>
              <li>Previous versions may be disabled or rendered inoperable</li>
            </ul>
          </li>
        </ul>

        <h3>2.2 Analytics and Data Collection</h3>
        <p>When using our Accessibility Widget, we collect the following analytics data:</p>
        <ul>
          <li>Usage Data:
            <ul>
              <li>Widget interaction events (open, close, settings changes)</li>
              <li>Feature usage (which features are enabled/disabled)</li>
              <li>Analysis events and results</li>
              <li>Session duration and frequency</li>
            </ul>
          </li>
          <li>Website Information:
            <ul>
              <li>Page URLs where the widget is implemented</li>
              <li>Page titles</li>
              <li>Implementation status and version</li>
            </ul>
          </li>
          <li>Technical Data:
            <ul>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Widget version and configuration</li>
              <li>Performance metrics</li>
            </ul>
          </li>
          <li>User Preferences:
            <ul>
              <li>Accessibility settings selected</li>
              <li>Language preferences</li>
              <li>Theme selections</li>
            </ul>
          </li>
        </ul>
        <p>This data is used to:</p>
        <ul>
          <li>Improve widget functionality and performance</li>
          <li>Monitor usage patterns and feature adoption</li>
          <li>Identify and fix technical issues</li>
          <li>Provide better support and updates</li>
          <li>Ensure compliance with our terms of service</li>
        </ul>

        <h3>2.3 Accessibility Enhancement Disclaimer</h3>
        <p>
          Our widget is designed to help enhance website accessibility through various customization options. However:
        </p>
        <ul>
          <li>We make no claims about compliance with any specific accessibility standards</li>
          <li>The widget is a supplementary tool and does not guarantee website accessibility</li>
          <li>Website owners remain responsible for their own accessibility compliance</li>
          <li>We recommend consulting accessibility experts for compliance needs</li>
        </ul>

        <h3>2.4 Technical Requirements</h3>
        <p>For optimal widget functionality:</p>
        <ul>
          <li>Supported browsers: Chrome and Firefox (latest versions)</li>
          <li>JavaScript must be enabled</li>
          <li>Local storage must be available for saving preferences</li>
        </ul>

        <h3>2.5 Chat Support Widget</h3>
        <ul>
          <li>AI responses are provided on an "as is" basis</li>
          <li>You are responsible for reviewing and moderating AI responses</li>
          <li>Usage quotas and rate limits apply based on your subscription</li>
          <li>Chat data retention policies must be communicated to your users</li>
          <li>You must not use the widget for prohibited content or purposes</li>
        </ul>

        <h3>2.6 Digital Portfolio Services</h3>
        <ul>
          <li>You retain ownership of your portfolio content</li>
          <li>We maintain ownership of the underlying code and infrastructure</li>
          <li>Hosting services are subject to continuous payment</li>
          <li>Portfolios must not contain illegal or infringing content</li>
          <li>Backup and restoration services are provided on a best-effort basis</li>
          <li>We reserve the right to remove portfolios that violate our terms without notice</li>
          <li>Portfolio URLs and subdomains remain our property and may be reassigned</li>
          <li>Migration or transfer of portfolios requires our written consent</li>
          <li>We may cache, copy, or store portfolio content for technical purposes</li>
          <li>You grant us perpetual license to use portfolio in our marketing</li>
        </ul>

        <h3>2.7 Automated Testing</h3>
        <p>For automated testing services, please visit <a href="https://www.sourcetrooper.com">www.sourcetrooper.com</a></p>
      </section>

      <section>
        <h2>3. Account Terms</h2>
        <ul>
          <li>You must provide accurate account information</li>
          <li>You are responsible for maintaining account security</li>
          <li>Accounts may not be shared between organizations</li>
          <li>We reserve the right to terminate accounts for violations</li>
        </ul>
      </section>

      <section>
        <h2>4. Payment Terms</h2>
        <ul>
          <li>Fees are non-refundable unless otherwise stated</li>
          <li>Subscription renewals are automatic unless cancelled</li>
          <li>Price changes will be notified 30 days in advance</li>
          <li>Failed payments may result in service suspension</li>
          <li>Custom development work requires separate agreements</li>
        </ul>
      </section>

      <section>
        <h2>5. Intellectual Property</h2>
        <ul>
          <li>Our products and services, including but not limited to the Accessibility Widget, are protected by intellectual property rights including copyrights, trade secrets, and proprietary technology</li>
          <li>All code, algorithms, designs, and implementations are the exclusive property of TearFace Digital</li>
          <li>You retain rights to your content and data, but not to our technology or implementation</li>
          <li>You grant us license to host and display your content</li>
          <li>We may feature your portfolio in our showcase with permission</li>
          <li>Any attempt to reverse engineer, decompile, or disassemble our software is strictly prohibited</li>
          <li>You may not create derivative works based on our software</li>
          <li>You may not remove or alter any proprietary notices or labels</li>
          <li>Any unauthorized use of our software will result in immediate termination of your license and may lead to legal action</li>
          <li>We reserve the right to audit your usage of our software to ensure compliance with these terms</li>
          <li>Any breach of these intellectual property terms will result in immediate termination of service and may lead to legal action for damages</li>
        </ul>
      </section>

      <section>
        <h2>6. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Use our services for illegal purposes</li>
          <li>Share API keys or account access</li>
          <li>Attempt to bypass usage limitations</li>
          <li>Interfere with service functionality</li>
          <li>Upload malicious content or code</li>
        </ul>
      </section>

      <section>
        <h2>7. Health, Medical, and Accessibility Liability</h2>
        <p>
          YOU EXPRESSLY UNDERSTAND AND AGREE THAT:
        </p>
        <ul>
          <li>Medical and Health Risks:
            <ul>
              <li>Our accessibility features are not medical devices or health aids</li>
              <li>We do not provide medical advice or health-related services</li>
              <li>Users with medical conditions should consult healthcare professionals</li>
              <li>We are not responsible for any health-related issues or medical emergencies</li>
              <li>Users should not rely on our services for critical medical or health needs</li>
            </ul>
          </li>
          <li>Accessibility Limitations:
            <ul>
              <li>Our widget may not be suitable for all accessibility needs</li>
              <li>Some users may experience adverse effects from certain features</li>
              <li>We do not guarantee improvement in accessibility conditions</li>
              <li>Users should test features before relying on them</li>
              <li>We are not liable for any worsening of accessibility conditions</li>
            </ul>
          </li>
          <li>Emergency Situations:
            <ul>
              <li>Our services are not designed for emergency situations</li>
              <li>Users should not rely on our services for emergency access</li>
              <li>We are not responsible for any delays or failures in emergency situations</li>
              <li>Users should have alternative accessibility solutions for emergencies</li>
            </ul>
          </li>
          <li>Liability Waiver:
            <ul>
              <li>You waive all claims related to health, medical, or accessibility issues</li>
              <li>We are not liable for any physical, mental, or emotional harm</li>
              <li>No claims can be made for medical expenses or health-related damages</li>
              <li>This waiver applies to all users and third parties</li>
            </ul>
          </li>
        </ul>
      </section>

      <section>
        <h2>8. Termination</h2>
        <p>We may terminate or suspend access to our services:</p>
        <ul>
          <li>For violation of these terms</li>
          <li>For non-payment of fees</li>
          <li>By your request</li>
          <li>For service discontinuation (with notice)</li>
        </ul>
      </section>

      <section>
        <h2>9. Limitation of Liability and Remedies</h2>
        <p>
          YOU EXPRESSLY UNDERSTAND AND AGREE THAT:
        </p>
        <ul>
          <li>All services are provided "as is" and "as available" without any warranties of any kind</li>
          <li>Your sole and exclusive remedy for any issue with our services is to stop using them</li>
          <li>Under no circumstances will we be liable for any:
            <ul>
              <li>Monetary damages of any kind</li>
              <li>Loss of profits, revenue, or business opportunities</li>
              <li>Damage to hardware, software, or digital content</li>
              <li>Claims by any third party against you</li>
              <li>Health-related issues or medical expenses</li>
              <li>Accessibility-related damages or claims</li>
            </ul>
          </li>
          <li>If any dispute arises, you agree that:
            <ul>
              <li>The only remedy will be to request removal or modification of content</li>
              <li>No monetary compensation will be sought or awarded</li>
              <li>All disputes will be resolved through amicable discussion</li>
              <li>Mediation must be attempted before any legal action</li>
            </ul>
          </li>
          <li>Force majeure events (like natural disasters, pandemics, etc.) exempt us from all obligations</li>
          <li>If any court finds us liable despite these terms, our maximum total liability is limited to the amount you paid us in the last 12 months, or $100, whichever is less</li>
        </ul>
      </section>

      <section>
        <h2>10. Changes to Terms</h2>
        <p>
          We reserve the right to modify these terms at any time. Changes will be effective upon posting to our website. Continued use of our services constitutes acceptance of new terms.
        </p>
      </section>

      <section>
        <h2>11. Accessibility Compliance and Standards</h2>
        
        <h3>11.1 Standards and Guidelines</h3>
        <p>Our Services aim to help websites meet various accessibility standards, including:</p>
        <ul>
          <li>Web Content Accessibility Guidelines (WCAG) 2.1</li>
          <li>Americans with Disabilities Act (ADA) Title III</li>
          <li>Section 508 of the Rehabilitation Act</li>
          <li>European Accessibility Act (EAA/EN301549)</li>
          <li>Accessibility for Ontarians with Disabilities Act (AODA)</li>
        </ul>

        <h3>11.2 Implementation Requirements</h3>
        <p>To maintain compliance, you must:</p>
        <ul>
          <li>Keep the widget code up-to-date with our latest version</li>
          <li>Not modify or interfere with the widget's functionality</li>
          <li>Properly implement the widget according to our documentation</li>
          <li>Test widget functionality in your environment</li>
          <li>Report any compatibility issues promptly</li>
        </ul>

        <h3>11.3 Legal Compliance Disclaimer</h3>
        <p>
          While our Services are designed to enhance website accessibility:
        </p>
        <ul>
          <li>We do not guarantee full compliance with any specific standard or regulation</li>
          <li>Implementation of our Services does not transfer your compliance obligations to us</li>
          <li>You remain responsible for your website's overall accessibility</li>
          <li>Regular accessibility audits are recommended in addition to using our Services</li>
        </ul>

        <h3>11.4 Service Level Agreement</h3>
        <p>
          Our service level commitments are as follows:
        </p>
        <ul>
          <li>Core Services:
            <ul>
              <li>Widget functionality depends on browser and local storage availability</li>
              <li>Updates are released as needed through our CDN</li>
              <li>Support is provided during business hours</li>
              <li>Response times vary by subscription tier</li>
              <li>Priority support response times may vary based on issue complexity and volume</li>
            </ul>
          </li>
          <li>Exclusions:
            <ul>
              <li>Browser compatibility issues</li>
              <li>User's internet connection</li>
              <li>Scheduled maintenance</li>
              <li>Force majeure events</li>
              <li>Third-party service disruptions</li>
              <li>User's device or browser limitations</li>
            </ul>
          </li>
          <li>Reporting and Analytics:
            <ul>
              <li>Monthly usage reports provided on a best-effort basis</li>
              <li>Reports may be delayed or incomplete</li>
              <li>We do not guarantee the accuracy or completeness of any reports or analytics</li>
              <li>Reports are for informational purposes only and should not be relied upon for legal compliance</li>
              <li>Any claims regarding report accuracy or uptime must be reported within 30 days of occurrence</li>
              <li>We reserve the right to modify report formats and metrics without notice</li>
              <li>No refunds or credits will be issued for report delays or inaccuracies</li>
              <li>Monthly insights and reports are estimates and may not reflect exact usage patterns</li>
            </ul>
          </li>
          <li>Feature Availability:
            <ul>
              <li>Early access features may be modified or delayed</li>
              <li>Features may be discontinued based on feedback</li>
              <li>No specific performance outcomes guaranteed</li>
              <li>Service availability may be affected by external factors</li>
              <li>We reserve the right to modify or discontinue any feature at any time</li>
            </ul>
          </li>
          <li>Uptime and Performance:
            <ul>
              <li>Uptime guarantee applies to our core services only</li>
              <li>Service level commitments are provided on a best-effort basis</li>
              <li>We reserve the right to modify, suspend, or discontinue any service level commitment</li>
              <li>No compensation or refunds for failure to meet service level commitments</li>
              <li>Service level commitments may vary by subscription tier</li>
              <li>All commitments are subject to change with notice</li>
            </ul>
          </li>
        </ul>

        <h3>11.5 Additional Disclaimers</h3>
        <p>
          YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT:
        </p>
        <ul>
          <li>All features and services are provided "as is" without any warranties</li>
          <li>We make no guarantees about specific outcomes or results</li>
          <li>Service performance may vary based on individual circumstances</li>
          <li>We are not responsible for any business decisions made based on our services</li>
          <li>You are solely responsible for verifying the suitability of our services for your needs</li>
          <li>We reserve the right to modify or discontinue any feature without notice</li>
          <li>No compensation will be provided for service modifications or discontinuations</li>
          <li>Your use of our services is at your own risk</li>
        </ul>

        <h3>11.6 Reports and Analytics Disclaimer</h3>
        <p>
          Regarding our reporting and analytics features:
        </p>
        <ul>
          <li>All reports and analytics are provided "as is" without any warranties</li>
          <li>We do not guarantee the accuracy, completeness, or reliability of any reports</li>
          <li>Reports should not be used as the sole basis for business decisions</li>
          <li>We are not liable for any actions taken based on report data</li>
          <li>Report data may be affected by various factors including:
            <ul>
              <li>Browser compatibility issues</li>
              <li>User privacy settings</li>
              <li>Network connectivity problems</li>
              <li>Third-party service disruptions</li>
              <li>Data collection limitations</li>
            </ul>
          </li>
          <li>We reserve the right to modify or discontinue reporting features without notice</li>
          <li>No compensation will be provided for report inaccuracies or delays</li>
          <li>Users are responsible for verifying report accuracy independently</li>
        </ul>

        <h3>11.7 Technical Requirements</h3>
        <p>Your website must meet these minimum requirements:</p>
        <ul>
          <li>Chrome or Firefox (latest versions)</li>
          <li>Valid HTML structure</li>
          <li>JavaScript enabled</li>
          <li>Proper implementation of ARIA landmarks</li>
        </ul>

        <h3>11.8 Usage Restrictions</h3>
        <p>You may not use our Services to:</p>
        <ul>
          <li>Circumvent existing accessibility features</li>
          <li>Implement conflicting accessibility solutions</li>
          <li>Create confusion for users of assistive technologies</li>
          <li>Generate misleading accessibility statements</li>
          <li>Violate any accessibility laws or regulations</li>
        </ul>
      </section>

      <section>
        <h2>12. Support and Updates</h2>
        
        <h3>12.1 Technical Support</h3>
        <ul>
          <li>Email support during business hours</li>
          <li>Self-service documentation and implementation guides</li>
          <li>Community support forums</li>
          <li>Online knowledge base</li>
        </ul>

        <h3>12.2 Maintenance and Updates</h3>
        <ul>
          <li>Security updates as needed</li>
          <li>Browser compatibility updates</li>
          <li>Feature enhancements based on user feedback</li>
          <li>Notice provided for significant changes</li>
        </ul>
      </section>

      <section>
        <h2>13. Governing Law and Jurisdiction</h2>
        <p>
          THESE TERMS AND YOUR USE OF OUR SERVICES SHALL BE GOVERNED BY AND CONSTRUED IN ACCORDANCE WITH THE LAWS OF THE JURISDICTION WHERE TEARFACE IS LOCATED, WITHOUT REGARD TO ITS CONFLICT OF LAW PROVISIONS. YOU EXPRESSLY AGREE THAT:
        </p>
        <ul>
          <li>Exclusive Jurisdiction:
            <ul>
              <li>Any legal action or proceeding shall be brought exclusively in the courts of the jurisdiction where TearFace is located</li>
              <li>You consent to the jurisdiction of such courts and waive any objection to venue</li>
              <li>You waive any right to a trial by jury</li>
              <li>You agree that service of process may be made by certified mail, return receipt requested</li>
              <li>We reserve the right to change our jurisdiction of operation at any time</li>
            </ul>
          </li>
          <li>Class Action Waiver:
            <ul>
              <li>You waive any right to participate in class actions</li>
              <li>You waive any right to consolidate claims with others</li>
              <li>You waive any right to bring claims as a representative or member of a class</li>
              <li>You waive any right to bring claims as a private attorney general</li>
              <li>You agree that any claims must be brought individually</li>
            </ul>
          </li>
          <li>Time Limitation:
            <ul>
              <li>Any claim must be brought within six (6) months of the date the claim arose</li>
              <li>You waive any statute of limitations to the contrary</li>
              <li>This time limitation applies to all claims, whether based on contract, tort, or other legal theory</li>
              <li>No claims can be brought after this period under any circumstances</li>
            </ul>
          </li>
          <li>Severability:
            <ul>
              <li>If any part of this section is found unenforceable, the remaining parts remain in effect</li>
              <li>If a court finds any part unenforceable, it shall be modified to the minimum extent necessary</li>
              <li>No court shall have the power to modify these terms</li>
            </ul>
          </li>
        </ul>
      </section>

      <section>
        <h2>14. Mandatory Arbitration and Dispute Resolution</h2>
        <p>
          YOU EXPRESSLY AGREE THAT ANY DISPUTE, CLAIM, OR CONTROVERSY ARISING OUT OF OR RELATING TO THESE TERMS OR THE BREACH, TERMINATION, ENFORCEMENT, INTERPRETATION, OR VALIDITY THEREOF, SHALL BE RESOLVED BY BINDING ARBITRATION:
        </p>
        <ul>
          <li>Arbitration Rules:
            <ul>
              <li>Arbitration shall be conducted by an arbitration body of our choosing</li>
              <li>We reserve the right to select the arbitration body and rules</li>
              <li>The arbitrator's decision shall be final and binding</li>
              <li>Judgment on the award may be entered in any court having jurisdiction</li>
              <li>You waive any right to challenge the selection of the arbitration body</li>
            </ul>
          </li>
          <li>Arbitration Process:
            <ul>
              <li>Arbitration shall be conducted in English</li>
              <li>Each party shall bear its own costs</li>
              <li>The arbitrator may award reasonable attorneys' fees to the prevailing party</li>
              <li>We reserve the right to change the location of arbitration</li>
              <li>All proceedings shall be confidential</li>
            </ul>
          </li>
          <li>Exceptions:
            <ul>
              <li>Small claims court cases are exempt from mandatory arbitration</li>
              <li>Intellectual property disputes may be brought in court</li>
              <li>Injunctive relief may be sought in court</li>
              <li>We reserve the right to bring any claims in court</li>
            </ul>
          </li>
          <li>Waiver of Rights:
            <ul>
              <li>You waive your right to a trial by jury</li>
              <li>You waive your right to participate in class actions</li>
              <li>You waive your right to bring claims as a private attorney general</li>
              <li>You waive your right to consolidate claims with others</li>
              <li>You waive any right to appeal arbitration decisions</li>
            </ul>
          </li>
          <li>Pre-Arbitration Requirements:
            <ul>
              <li>Before pursuing any legal action, you must:
                <ul>
                  <li>Contact us first with any concerns</li>
                  <li>Allow us 30 days to address your concerns</li>
                  <li>Participate in good-faith negotiations</li>
                  <li>Attempt mediation through a mutually agreed third party</li>
                </ul>
              </li>
              <li>No legal action may be taken until these steps are completed</li>
              <li>We reserve the right to choose the mediator</li>
              <li>All discussions must be in English</li>
            </ul>
          </li>
        </ul>
      </section>

      <section>
        <h2>15. Third-Party Services</h2>
        <p>
          Some features of our website may link to or integrate with third-party services.
        </p>
        <ul>
          <li>Third-party integrations may have their own terms of service and privacy policies</li>
          <li>We are not responsible for the content, privacy practices, or policies of third-party services</li>
          <li>Your use of third-party services is subject to their respective terms and conditions</li>
          <li>We recommend reviewing the terms of any third-party services you interact with through our platform</li>
        </ul>
      </section>

      <section>
        <h2>16. Contact Information</h2>
        <p>For questions about these Terms of Service, please contact:</p>
        <p>Email: hellotearface@gmail.com</p>
      </section>

      <section>
        <h2>17. Dispute Resolution</h2>
        <p>
          BY USING OUR SERVICES, YOU AGREE TO THE FOLLOWING DISPUTE RESOLUTION PROCESS:
        </p>
        <ul>
          <li>Before Taking Action:
            <ul>
              <li>Contact us first with any concerns at hellotearface@gmail.com</li>
              <li>Allow us 30 days to address your concerns</li>
              <li>Participate in good-faith discussions to resolve the issue</li>
            </ul>
          </li>
          <li>Resolution Process:
            <ul>
              <li>Step 1: Informal discussion and resolution attempt</li>
              <li>Step 2: Written notice detailing specific concerns</li>
              <li>Step 3: Mediation through a mutually agreed third party</li>
              <li>Step 4: Only after these steps may other action be considered</li>
            </ul>
          </li>
          <li>Acceptable Remedies:
            <ul>
              <li>Modification or removal of content</li>
              <li>Changes to service functionality</li>
              <li>Termination of service</li>
              <li>No monetary remedies are available</li>
            </ul>
          </li>
        </ul>
      </section>

      <section>
        <h2>18. No Waiver and Severability</h2>
        <p>
          If any part of these Terms of Service is found to be invalid, unenforceable, or incomplete, the remaining provisions will remain in full force and effect. Our failure to enforce any right or provision of these terms does not constitute a waiver of that right or provision. No omission, error, or imperfection in these documents will create any liability or waive any limitation of liability, and you agree that our liability remains limited as described herein regardless of any such issue.
        </p>
      </section>

      <section>
        <h2>19. Marketing and Statistics Disclaimer</h2>
        <p>
          YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT:
        </p>
        <ul>
          <li>Any statistics, numbers, percentages, or metrics displayed on our website or marketing materials:
            <ul>
              <li>Are for illustrative purposes only</li>
              <li>May be based on industry averages, third-party studies, or general market research</li>
              <li>Do not constitute guarantees or promises of specific results</li>
              <li>May vary based on individual circumstances, implementation, and market conditions</li>
              <li>Should not be relied upon for business, legal, or investment decisions</li>
              <li>Often represent best-case scenarios under optimal conditions</li>
              <li>May be rounded or simplified for clarity and presentation</li>
            </ul>
          </li>
          <li>Portfolio Performance Statistics:
            <ul>
              <li>Metrics like "3× More Interviews" or "80% Higher Engagement" are based on:</li>
              <li>Selected case studies where clients reported positive outcomes</li>
              <li>Comparative analysis against clients' previous portfolios</li>
              <li>Specific time periods that may not represent long-term performance</li>
              <li>Limited sample sizes that may not be statistically significant</li>
            </ul>
          </li>
          <li>Marketing Claims and Testimonials:
            <ul>
              <li>Represent selected successful cases but individual results may vary significantly</li>
              <li>Are not guaranteed for every user or implementation</li>
              <li>May be based on aggregated or estimated data</li>
              <li>Should be independently verified for your specific use case</li>
              <li>May combine results from different time periods or market conditions</li>
              <li>Could become outdated as market conditions change</li>
            </ul>
          </li>
          <li>You Expressly Agree That:
            <ul>
              <li>No specific performance outcomes are guaranteed</li>
              <li>You will not make business decisions solely based on our marketing materials</li>
              <li>We are not liable for any decisions made based on marketing statistics</li>
              <li>Your actual results may differ from any examples or case studies shown</li>
              <li>Past performance does not guarantee future results</li>
              <li>Market conditions and competitive factors may affect outcomes</li>
              <li>Individual effort and implementation quality impact results</li>
            </ul>
          </li>
          <li>Third-Party Statistics:
            <ul>
              <li>Industry statistics and market research cited in our materials:</li>
              <li>Come from various third-party sources we believe to be reliable</li>
              <li>May not have been independently verified by us</li>
              <li>Could be subject to change or revision</li>
              <li>Should be verified through original sources when possible</li>
            </ul>
          </li>
        </ul>
      </section>

      <section>
        <h2>20. AI-Generated Content and Artwork</h2>
        <p>
          YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT:
        </p>
        <ul>
          <li>Artwork and Images:
            <ul>
              <li>Some artwork, images, and visual elements on our website and marketing materials are generated using artificial intelligence (AI) technologies</li>
              <li>AI-generated content is used for illustrative and aesthetic purposes only</li>
              <li>We do not claim ownership of the artistic styles or original artworks that may have influenced our AI-generated content</li>
              <li>AI-generated images are meant to represent concepts and ideas, not specific artworks or artists</li>
              <li>The appearance, style, and characteristics of AI-generated content may change or evolve over time</li>
              <li>We make no guarantees about the uniqueness, originality, or consistency of AI-generated content</li>
            </ul>
          </li>
          <li>Creative and Artistic Claims:
            <ul>
              <li>References to "artistry," "artistic vision," or similar terms describe our approach to design and technology</li>
              <li>Such descriptions are subjective and interpretative statements about our design philosophy</li>
              <li>We do not claim to replicate or replace traditional artistic processes</li>
              <li>Any comparison to traditional art or artistic processes is metaphorical and for illustrative purposes</li>
              <li>Our use of terms like "creative," "artistic," or "design" refers to our technical and professional services, not traditional fine arts</li>
              <li>Any artistic elements in our services are intended to enhance user experience and visual appeal, not to create fine art</li>
            </ul>
          </li>
          <li>Usage Rights and Attribution:
            <ul>
              <li>All AI-generated content is either created through properly licensed AI tools or with appropriate permissions</li>
              <li>We make no claims of exclusivity or unique ownership of the visual styles produced by AI</li>
              <li>The underlying AI models and technologies belong to their respective owners</li>
              <li>We provide this disclosure in the interest of transparency and proper attribution</li>
              <li>You acknowledge that AI-generated content may unintentionally resemble existing works</li>
              <li>We reserve the right to modify, replace, or remove AI-generated content at any time</li>
            </ul>
          </li>
          <li>Portfolio and Client Work:
            <ul>
              <li>Unless explicitly stated otherwise, portfolio samples and client work showcased on our website do not use AI-generated content</li>
              <li>Any AI-generated elements in portfolio work will be clearly labeled as such</li>
              <li>Client projects may incorporate AI-generated content only with explicit client consent</li>
              <li>We maintain clear documentation of which elements are AI-generated in client deliverables</li>
            </ul>
          </li>
          <li>Third-Party Content and Resources:
            <ul>
              <li>Our website and services may incorporate content from third-party sources including:
                <ul>
                  <li>Stock images and illustrations from licensed providers</li>
                  <li>Icons, graphics, and design elements from various sources</li>
                  <li>Fonts and typography from font services</li>
                  <li>Open-source components and libraries</li>
                </ul>
              </li>
              <li>All third-party content is used in accordance with:
                <ul>
                  <li>Applicable licenses and terms of use</li>
                  <li>Attribution requirements where mandated</li>
                  <li>Fair use principles where applicable</li>
                </ul>
              </li>
              <li>Some visual elements may be:
                <ul>
                  <li>Modified or adapted from their original form</li>
                  <li>Combined with other elements or AI-generated content</li>
                  <li>Used in ways permitted by their respective licenses</li>
                </ul>
              </li>
              <li>We maintain internal records of:
                <ul>
                  <li>Content sources and applicable licenses</li>
                  <li>Required attributions and usage restrictions</li>
                  <li>Modification and adaptation permissions</li>
                </ul>
              </li>
            </ul>
          </li>
        </ul>
      </section>

      <section>
        <h2>21. Inclusivity, Usability, and Product Improvement</h2>
        <p>
          Our mission is to make technology more useful, accessible, and less complicated for a broad range of users and organizations through practical design improvements, automated testing, and supportive tools.
        </p>
        <ul>
          <li>Our mission is to make technology more useful, accessible, and less complicated for a broad range of users and organizations through practical design improvements, usability testing, and supportive tools.</li>
          <li>We offer products and services—including accessibility widgets, AI chat support, usability and automated testing solutions, UX/UI design assistance, and digital optimization tools—intended to help clients improve their technology's usefulness and user experience.</li>
          <li>Automated testing services are designed to identify bugs, usability issues, and areas for improvement, providing clients with actionable reports. These services do not guarantee the detection of all issues or compliance with any specific standards.</li>
          <li>AI chat support is intended to assist website users with common queries, FAQs, and navigation, helping to simplify user interactions. It is not a substitute for comprehensive customer support, professional advice, or legal compliance guidance.</li>
          <li>References to "for everyone," "for all," or similar terms reflect our commitment to promoting broader usability, simplified interfaces, and enhanced value, but do not constitute a guarantee that every product, service, or recommendation will be universally accessible, suitable, or effective for every individual, business, or specific use case.</li>
          <li>Our services focus on practical improvements to user experience, visual design, and functional usability rather than ensuring compliance with specific accessibility standards, regulations, or legal requirements such as ADA, WCAG, or Section 508 guidelines.</li>
          <li>Design and user experience improvements, including UX/UI enhancements, prioritize usability, visual appeal, and user satisfaction rather than regulatory compliance or meeting specific accessibility certification requirements.</li>
          <li>We do not warrant that our products or services will meet the needs of all users, achieve universal accessibility, ensure compliance with any specific standards or regulations, or completely eliminate technical complexity.</li>
          <li>Effectiveness of our solutions may vary based on factors including but not limited to: client's existing technology stack, third-party integrations, user device capabilities, assistive technology compatibility, implementation approach, and ongoing maintenance practices.</li>
          <li>Any improvements, recommendations, or solutions we provide are offered in good faith based on general best practices, but ultimate responsibility for implementation, regulatory compliance, legal requirements, and suitability for specific use cases remains entirely with the client or user.</li>
          <li>Clients are responsible for conducting their own accessibility audits, compliance assessments, user testing, and legal reviews as needed for their specific requirements and jurisdictions.</li>
          <li>We expressly disclaim any liability for failure to achieve specific accessibility standards, usability benchmarks, regulatory compliance, or business outcomes, and for any limitations inherent in third-party platforms, technologies, client content, or implementation choices.</li>
        </ul>
      </section>

      <section>
        <h2>22. Credits and Attributions</h2>
        <p>
          The following credits and attributions apply to resources used on this website:
        </p>
        <ul>
          <li>
            <strong>Website Design & Development:</strong> Built by TearFaceDigital.
          </li>
          <li>
            <strong>Stock Images & Illustrations:</strong> Some images on this website are provided by Freepik (<a href="https://www.freepik.com" target="_blank" rel="noopener noreferrer">www.freepik.com</a>) and used under their free license. Where required, attribution is provided near the asset or in this section.
          </li>
          <li>
            <strong>Icons & Graphics:</strong> Icons and SVGs are sourced from AI-generated tools, Freepik, and other third-party providers, and are used in accordance with their respective licenses.
          </li>
          <li>
            <strong>Fonts & Open Source:</strong> Fonts and open-source libraries are used under their respective licenses.
          </li>
          <li>
            <strong>Favicon & Icon Generation:</strong> Favicons and some icons were generated using RealFaviconGenerator (<a href="https://realfavicongenerator.net" target="_blank" rel="noopener noreferrer">realfavicongenerator.net</a>).
          </li>
        </ul>
        <p>
          For further information or specific attribution requests, please contact us at hellotearface@gmail.com.
        </p>
      </section>

      <section>
        <h2>23. Additional Protections and Waivers</h2>
        <p>
          YOU EXPRESSLY ACKNOWLEDGE AND IRREVOCABLY AGREE THAT:
        </p>
        <ul>
          <li>Complete Waiver of Rights:
            <ul>
              <li>You waive all rights to sue, claim, or demand compensation from TearFace</li>
              <li>You waive all rights to join or initiate any class action or mass action</li>
              <li>You waive all rights to bring claims in any forum other than specified herein</li>
              <li>You waive all rights to challenge the validity or enforceability of these terms</li>
              <li>You waive all rights to seek injunctive or equitable relief</li>
              <li>You waive all rights to appeal any decision</li>
            </ul>
          </li>
          <li>No Guarantees:
            <ul>
              <li>We make no guarantees about service availability, performance, or results</li>
              <li>We make no guarantees about accessibility compliance or improvements</li>
              <li>We make no guarantees about data security or privacy</li>
              <li>We make no guarantees about third-party integrations</li>
              <li>All services are provided "as is" without any warranties</li>
            </ul>
          </li>
          <li>Indemnification:
            <ul>
              <li>You agree to indemnify us against all claims, damages, and expenses</li>
              <li>You agree to pay our legal fees if you bring any claims</li>
              <li>You agree to pay our costs of defending any claims</li>
              <li>This indemnification survives termination of service</li>
            </ul>
          </li>
          <li>Force Majeure:
            <ul>
              <li>We are not liable for any failure due to circumstances beyond our control</li>
              <li>This includes but is not limited to:
                <ul>
                  <li>Natural disasters</li>
                  <li>War, terrorism, or civil unrest</li>
                  <li>Government actions or regulations</li>
                  <li>Technical failures or cyber attacks</li>
                  <li>Third-party service disruptions</li>
                </ul>
              </li>
            </ul>
          </li>
          <li>Service Modifications:
            <ul>
              <li>We may modify or discontinue any service at any time</li>
              <li>We may change pricing, features, or terms without notice</li>
              <li>No refunds or compensation for service changes</li>
              <li>Your continued use constitutes acceptance of changes</li>
            </ul>
          </li>
          <li>Data and Privacy:
            <ul>
              <li>We make no guarantees about data security</li>
              <li>We may modify our data practices without notice</li>
              <li>You are responsible for backing up your data</li>
              <li>We may delete data at any time</li>
            </ul>
          </li>
          <li>Third-Party Services:
            <ul>
              <li>We are not responsible for third-party services</li>
              <li>We make no guarantees about third-party integrations</li>
              <li>You use third-party services at your own risk</li>
              <li>We may remove third-party integrations at any time</li>
            </ul>
          </li>
          <li>Termination:
            <ul>
              <li>We may terminate service at any time without cause</li>
              <li>No refunds upon termination</li>
              <li>We may delete all data upon termination</li>
              <li>You must cease all use upon termination</li>
            </ul>
          </li>
        </ul>
      </section>

      <section>
        <h2>24. Final Waiver and Release</h2>
        <p>
          BY USING OUR SERVICES, YOU HEREBY IRREVOCABLY AND UNCONDITIONALLY:
        </p>
        <ul>
          <li>Release and forever discharge TearFace from any and all claims, demands, and causes of action</li>
          <li>Waive any and all rights to sue, claim, or demand compensation</li>
          <li>Agree that these terms are a complete defense to any claim</li>
          <li>Acknowledge that you have read and understood all terms</li>
          <li>Agree that these terms are fair and reasonable</li>
          <li>Accept all risks associated with using our services</li>
          <li>Agree that no oral or written representations have been made</li>
          <li>Confirm that you are of legal age and capacity to agree to these terms</li>
        </ul>
        <p>
          IF YOU DO NOT AGREE TO ANY PART OF THESE TERMS, YOU MUST CEASE USING ALL TEARFACE SERVICES IMMEDIATELY.
        </p>
      </section>
    </div>
  );
};

export default TermsOfService;
